import { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { REACT_APP_UPLOADS } from '../../../constants';
import { BASEMAPS } from '../../../constants/basemaps';
import { splitSpecies } from '../../../utils/species';

/* ── Icons ───────────────────────────────────────────────────────── */
const pulsingIcon = L.divIcon({
  className:   '',
  html:        '<div class="wits-pulse-dot"></div>',
  iconSize:    [16, 16],
  iconAnchor:  [8, 8],
  popupAnchor: [0, -10],
});

function makeClusterIcon(count) {
  const size = count > 50 ? 48 : count > 20 ? 40 : 32;
  return L.divIcon({
    className: '',
    html: `<div class="wits-cluster" style="width:${size}px;height:${size}px;font-size:${size > 40 ? 13 : 11}px">${count}</div>`,
    iconSize:   [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/* ── Grid clustering ─────────────────────────────────────────────── */
function clusterByGrid(points, gridSize) {
  const cells = {};
  points.forEach((p) => {
    const key = `${Math.floor(p.latitude / gridSize)}_${Math.floor(p.longitude / gridSize)}`;
    if (!cells[key]) cells[key] = [];
    cells[key].push(p);
  });
  return Object.values(cells).map((pts) => ({
    points: pts,
    count:  pts.length,
    lat:    pts.reduce((s, p) => s + p.latitude,  0) / pts.length,
    lng:    pts.reduce((s, p) => s + p.longitude, 0) / pts.length,
  }));
}

/* ── Species display helper ──────────────────────────────────────── */
function formatSpecies(raw) {
  if (!raw) return '—';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.join(', ');
  } catch (_) { /* fall through — raw is a ";"-delimited string, not JSON */ }
  const species = splitSpecies(raw);
  return species.length ? species.join(', ') : raw;
}

/* ── Popup: single point ─────────────────────────────────────────── */
function PointPopup({ d }) {
  const rows = [
    ['Species',     formatSpecies(d.species)],
    ['Location',    d.location      || '—'],
    ['Date',        d.dateOfSeizure || '—'],
    ['Weight',      d.weight ? `${d.weight} kg` : '—'],
    ['Charge',      d.charge1       || '—'],
    ['Nationality', d.nationality   || '—'],
  ];
  return (
    <div style={{
      fontFamily: "'DM Sans',system-ui,sans-serif",
      background: '#0a1a0f',
      border: '1px solid rgba(34,197,94,0.25)',
      borderRadius: 10,
      padding: '14px 16px',
      minWidth: 210,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    }}>
      <p style={{ fontSize: 10, color: '#4ade80', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 10px', fontWeight: 600 }}>
        Incident Record
      </p>
      <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: '#6ee7b7', padding: '3px 10px 3px 0', whiteSpace: 'nowrap', verticalAlign: 'top', fontWeight: 500 }}>{k}</td>
              <td style={{ color: 'rgba(255,255,255,0.6)', padding: '3px 0' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        onClick={() => { window.location.href = '/dashboard'; }}
        style={{
          marginTop: 12, width: '100%',
          background: 'rgba(22,101,52,0.25)', border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 6, color: '#4ade80', padding: '7px 0',
          fontFamily: "'DM Sans',system-ui,sans-serif", fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}
      >
        View Full Record &rarr;
      </button>
    </div>
  );
}

/* ── Popup: cluster ──────────────────────────────────────────────── */
function ClusterPopup({ points }) {
  const preview = points.slice(0, 3);
  return (
    <div style={{
      fontFamily: "'DM Sans',system-ui,sans-serif",
      background: '#0a1a0f',
      border: '1px solid rgba(34,197,94,0.25)',
      borderRadius: 10,
      padding: '14px 16px',
      minWidth: 210,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    }}>
      <p style={{ fontSize: 10, color: '#4ade80', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 8px', fontWeight: 600 }}>
        {points.length} Incidents &mdash; Zoom to expand
      </p>
      {preview.map((d, i) => (
        <div key={i} style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', padding: '4px 0', borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.07)' }}>
          <span style={{ color: '#6ee7b7', fontWeight: 500 }}>{formatSpecies(d.species)}</span>
          {d.location ? ` · ${d.location}` : ''}
        </div>
      ))}
      {points.length > 3 && (
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
          +{points.length - 3} more &mdash; zoom in to view individually
        </p>
      )}
      <button
        onClick={() => { window.location.href = '/dashboard'; }}
        style={{
          marginTop: 10, width: '100%',
          background: 'rgba(22,101,52,0.25)', border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 6, color: '#4ade80', padding: '7px 0',
          fontFamily: "'DM Sans',system-ui,sans-serif", fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}
      >
        View All Records &rarr;
      </button>
    </div>
  );
}

/* ── Zoom-aware clustered markers (must live inside MapContainer) ── */
function ClusteredMarkers({ validPoints }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [map]);

  const gridSize =
    zoom <= 4  ? 2.0  :
    zoom <= 6  ? 1.0  :
    zoom <= 8  ? 0.35 :
    zoom <= 10 ? 0.12 : 0.04;

  const clusters = useMemo(
    () => clusterByGrid(validPoints, gridSize),
    [validPoints, gridSize]
  );

  return (
    <>
      {clusters.map((c, i) =>
        c.count === 1 ? (
          <Marker key={i} position={[c.lat, c.lng]} icon={pulsingIcon}>
            <Popup className="wits-popup" maxWidth={250}>
              <PointPopup d={c.points[0]} />
            </Popup>
          </Marker>
        ) : (
          <Marker key={i} position={[c.lat, c.lng]} icon={makeClusterIcon(c.count)}>
            <Popup className="wits-popup" maxWidth={260}>
              <ClusterPopup points={c.points} />
            </Popup>
          </Marker>
        )
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */

const GlobeMap = () => {
  const [allData,    setAllData]    = useState([]);
  const [totalCases, setTotalCases] = useState(0);

  // ── Fetch all cases for map pins ────────────────────────────────
  useEffect(() => {
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/all-cases`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setAllData(res.data);
          setTotalCases(res.data.length);
        }
      })
      .catch(() => { /* map renders empty until data arrives */ });
  }, []);

  // ── Only plot records that have coordinates ──────────────────────
  const validPoints = useMemo(
    () => allData.filter((d) => d.latitude != null && d.longitude != null),
    [allData]
  );

  // ── Count distinct species from live data ────────────────────────
  const uniqueSpecies = useMemo(() => {
    const set = new Set();
    allData.forEach((d) => {
      if (!d.species) return;
      try {
        const arr = JSON.parse(d.species);
        if (Array.isArray(arr)) {
          arr.forEach((s) => splitSpecies(s).forEach((sp) => set.add(sp.toLowerCase())));
          return;
        }
      } catch (_) { /* not JSON — d.species is a ";"-delimited string */ }
      splitSpecies(d.species).forEach((sp) => set.add(sp.toLowerCase()));
    });
    return set.size;
  }, [allData]);

  // ── Count distinct countries from live data ──────────────────────
  const uniqueCountries = useMemo(() => {
    const set = new Set();
    allData.forEach((d) => {
      const country = d.country?.trim();
      if (country) set.add(country.toLowerCase());
    });
    return set.size;
  }, [allData]);

  const stats = [
    { label: 'Seizures Recorded', value: totalCases ? `${totalCases}+` : '—'  },
    { label: 'Species Monitored', value: uniqueSpecies ? `${uniqueSpecies}+` : '—' },
    { label: 'Offenders Tracked', value: '2,400+'  },
    { label: 'Countries Active',  value: uniqueCountries ? `${uniqueCountries}+` : '—' },
  ];

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height: '80vh', background: '#010b05' }}
    >
      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 450,
          background:
            'radial-gradient(ellipse 88% 78% at 50% 50%, transparent 30%, rgba(0,0,0,0.5) 100%)',
        }}
      />

      {/* Map */}
      <MapContainer
        center={[15, 20]}
        zoom={2}
        minZoom={2}
        maxZoom={14}
        zoomControl={false}
        scrollWheelZoom={false}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        attributionControl={false}
      >
        <TileLayer
          url={BASEMAPS.dark.url}
          subdomains="abcd"
          maxZoom={19}
        />
        <ZoomControl position="topright" />
        <ClusteredMarkers validPoints={validPoints} />
      </MapContainer>

      {/* Stats panel — bottom left */}
      <div
        className="absolute"
        style={{
          bottom: 24, left: 24, zIndex: 1001,
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1px',
          background: 'rgba(34,197,94,0.1)',
          border: '1px solid rgba(34,197,94,0.15)',
          borderRadius: 10, overflow: 'hidden',
          backdropFilter: 'blur(12px)',
        }}
      >
        {stats.map((s) => (
          <div key={s.label} style={{ background: 'rgba(1,14,7,0.9)', padding: '12px 20px' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#4ade80', lineHeight: 1, margin: 0 }}>
              {s.value}
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'rgba(255,255,255,0.38)', marginTop: 4, letterSpacing: '0.04em' }}>
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {/* CTA — bottom right */}
      <div className="absolute" style={{ bottom: 24, right: 24, zIndex: 1001 }}>
        <button
          onClick={() => { window.location.href = '/dashboard'; }}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.05em',
            background: 'rgba(1,14,7,0.9)', border: '1px solid rgba(34,197,94,0.28)',
            borderRadius: 8, color: '#4ade80', padding: '10px 20px',
            cursor: 'pointer', backdropFilter: 'blur(12px)', transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background  = 'rgba(22,101,52,0.4)';
            e.currentTarget.style.borderColor = 'rgba(34,197,94,0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background  = 'rgba(1,14,7,0.9)';
            e.currentTarget.style.borderColor = 'rgba(34,197,94,0.28)';
          }}
        >
          Explore Full Dataset &rarr;
        </button>
      </div>
    </div>
  );
};

export default GlobeMap;
